import { arrayUnion, deleteField, writeBatch } from "firebase/firestore";
import { err, ok } from "@pack-it-up/shared";
import { ChunkedBatch } from "../chunkedBatch.js";
import { toCategoryDto } from "../mappers/category.mapper.js";
import { toItemDto } from "../mappers/item.mapper.js";
import { toTodoDto } from "../mappers/todo.mapper.js";
import { assignmentToDto, provenanceToDto } from "../mappers/common.js";
import { patchField, tagsPatch, toDomainError } from "./support.js";
// Applies a T05 ImportPlan as one optimistic client-side batch (Doc 07 §3) — the
// Firestore side of the `applyImportPlan` seam the in-memory bundle also implements.
// Fire-and-forget like every optimistic write: the repositories' snapshot streams
// deliver the authoritative result (or roll the local state back on rule denial).
export const makeImportApplier = (db, p) => (plan, tripId) => {
    // Firestore dropped the 500-op batch limit in 2023; the remaining bound is the
    // 10 MiB API request size, far above any realistic template import.
    const batch = writeBatch(db);
    for (const c of plan.categoryCreates) {
        batch.set(p.category(c.id), toCategoryDto(c));
    }
    for (const i of plan.itemCreates) {
        const cat = plan.categoryCreates.find((c) => c.id === i.categoryId);
        const listId = cat?.parentId ?? tripId;
        batch.set(p.item(i.id), { ...toItemDto(i), ...(listId !== undefined ? { listId } : {}) });
    }
    for (const t of plan.todoCreates) {
        const cat = plan.categoryCreates.find((c) => c.id === t.categoryId);
        const listId = cat?.parentId ?? tripId;
        batch.set(p.todo(t.id), { ...toTodoDto(t), ...(listId !== undefined ? { listId } : {}) });
    }
    // Doc 14 §4 — a refresh also pulls the template's edits into rows already on the
    // trip. Absent on a plain import, so an import batch is byte-for-byte what it was.
    if ("itemUpdates" in plan) {
        for (const u of plan.itemUpdates) {
            const out = {};
            patchField(out, "name", u.patch.name);
            patchField(out, "quantity", u.patch.quantity);
            patchField(out, "description", u.patch.description);
            patchField(out, "notes", u.patch.notes);
            patchField(out, "tags", tagsPatch(u.patch.tags));
            if (u.patch.assignment !== undefined)
                out.assignment = assignmentToDto(u.patch.assignment);
            if (Object.keys(out).length > 0)
                batch.update(p.item(String(u.id)), out);
        }
        for (const u of plan.todoUpdates) {
            const out = {};
            patchField(out, "name", u.patch.name);
            patchField(out, "description", u.patch.description);
            patchField(out, "notes", u.patch.notes);
            patchField(out, "tags", tagsPatch(u.patch.tags));
            patchField(out, "relativeDueOffset", u.patch.relativeDueOffset);
            patchField(out, "dueDate", u.patch.dueDate);
            if (u.patch.assignment !== undefined)
                out.assignment = assignmentToDto(u.patch.assignment);
            if (Object.keys(out).length > 0)
                batch.update(p.todo(String(u.id)), out);
        }
    }
    for (const patch of plan.provenancePatches) {
        const ref = patch.collection === "items" ? p.item(String(patch.id)) : p.todo(String(patch.id));
        batch.update(ref, {
            provenance: provenanceToDto(patch.provenance),
            templateIds: patch.templateIds,
        });
    }
    if (tripId !== undefined) {
        // Issue #185 — the templates' participants join the trip as members. Written as
        // dotted field paths so a concurrent membership change elsewhere in the map
        // survives, with `memberUserIds` (the queryable mirror, Doc 02 §9) union-ed for
        // the `user` ones. The planner only fills this when the importer owns the trip.
        const memberFields = {};
        const newUserIds = [];
        for (const { participantId, entry } of plan.memberCreates) {
            memberFields[`members.${participantId}`] = entry;
            if (entry.kind === "user")
                newUserIds.push(String(participantId));
        }
        batch.update(p.trip(tripId), {
            importedTemplateIds: plan.tripPatch.importedTemplateIds,
            ...memberFields,
            ...(newUserIds.length > 0 ? { memberUserIds: arrayUnion(...newUserIds) } : {}),
        });
    }
    void batch.commit();
};
// Issue #8 — applies a `CopyPlan`: the rows of a duplicated template, a template
// built from a trip, or a copied trip, all landing under a freshly created target
// list. Awaits and reports like the sync applier (the target isn't on screen yet, so
// the UI is the only thing that can report a failure) and chunks for the same reason
// — a copy is the size of the whole source list. Categories go first: their rows name
// them, and anyone already streaming the new list should see the container before its
// contents.
export const makeCopyApplier = (db, p) => async (plan) => {
    const batch = ChunkedBatch.forDb(db);
    for (const c of plan.categoryCreates)
        batch.set(p.category(c.id), toCategoryDto(c));
    // `listId` is the denormalised per-list query key (Doc 02 §8).
    for (const i of plan.itemCreates)
        batch.set(p.item(i.id), { ...toItemDto(i), listId: plan.targetId });
    for (const t of plan.todoCreates)
        batch.set(p.todo(t.id), { ...toTodoDto(t), listId: plan.targetId });
    try {
        await batch.commit();
        return ok(undefined);
    }
    catch (e) {
        return err(toDomainError(e));
    }
};
// Doc 13 §5, §8 — the sync-back counterpart. Two deliberate differences from the
// import applier above: it **awaits** its commits and returns a Result (nothing is
// streaming the template into the trip screen, so the UI is the only thing that can
// report a failure), and it **chunks**, via `ChunkedBatch` — a sync can touch an entire
// template plus a provenance patch on every trip row, well past a batch's 500-op cap.
export const makeTemplateSyncApplier = (db, p) => async (plan) => {
    // Enqueue order is preserved across chunks, which the category deletes below rely on.
    const batch = ChunkedBatch.forDb(db);
    for (const c of plan.categoryCreates)
        batch.set(p.category(c.id), toCategoryDto(c));
    for (const r of plan.categoryRenames)
        batch.update(p.category(r.id), { name: r.name });
    for (const i of plan.itemCreates)
        // listId is the denormalised per-list query key (Doc 02 §8); a row created by a
        // sync belongs to the template.
        batch.set(p.item(i.id), { ...toItemDto(i), listId: plan.templateId });
    for (const u of plan.itemUpdates) {
        const out = {};
        patchField(out, "name", u.patch.name);
        patchField(out, "quantity", u.patch.quantity);
        patchField(out, "description", u.patch.description);
        patchField(out, "notes", u.patch.notes);
        patchField(out, "tags", tagsPatch(u.patch.tags));
        if (u.patch.assignment !== undefined)
            out.assignment = assignmentToDto(u.patch.assignment);
        if (Object.keys(out).length > 0)
            batch.update(p.item(u.id), out);
    }
    for (const id of plan.itemDeletes)
        batch.delete(p.item(id));
    for (const t of plan.todoCreates)
        batch.set(p.todo(t.id), { ...toTodoDto(t), listId: plan.templateId });
    for (const u of plan.todoUpdates) {
        const out = {};
        patchField(out, "name", u.patch.name);
        patchField(out, "description", u.patch.description);
        patchField(out, "notes", u.patch.notes);
        patchField(out, "tags", tagsPatch(u.patch.tags));
        patchField(out, "relativeDueOffset", u.patch.relativeDueOffset);
        if (u.patch.assignment !== undefined)
            out.assignment = assignmentToDto(u.patch.assignment);
        if (Object.keys(out).length > 0)
            batch.update(p.todo(u.id), out);
    }
    for (const id of plan.todoDeletes)
        batch.delete(p.todo(id));
    // After the rows, so a category only disappears once it is empty (Doc 13 §5).
    for (const id of plan.categoryDeletes)
        batch.delete(p.category(id));
    for (const patch of plan.tripProvenancePatches) {
        const ref = patch.collection === "items" ? p.item(String(patch.id)) : p.todo(String(patch.id));
        batch.update(ref, {
            provenance: provenanceToDto(patch.provenance),
            templateIds: patch.templateIds,
        });
    }
    try {
        await batch.commit();
        return ok(undefined);
    }
    catch (e) {
        return err(toDomainError(e));
    }
};
// Issue #185 — the row cleanup that follows removing a traveler (a trip member or a
// template participant): delete the rows they were the sole allocatee of, and drop
// their allocation and recorded checks from the rest. Awaits and reports like the copy
// applier — the removal is a confirmed destructive action, so a failure has to surface.
//
// The completion entries go as dotted `deleteField()` paths rather than a rewritten
// map: `completions` is the structure concurrent editors touch constantly, and writing
// the whole map back would clobber a check landing at the same moment.
export const makeParticipantRemovalApplier = (db, p) => async (plan) => {
    const batch = ChunkedBatch.forDb(db);
    const ref = (collection, id) => collection === "items" ? p.item(id) : p.todo(id);
    for (const d of plan.deletes)
        batch.delete(ref(d.collection, String(d.id)));
    for (const patch of plan.patches) {
        const out = {};
        if (patch.assignment !== undefined)
            out.assignment = assignmentToDto(patch.assignment);
        if (patch.completions !== undefined)
            out[`completions.byParticipant.${plan.participantId}`] = deleteField();
        if (patch.staging !== undefined)
            out[`staging.byParticipant.${plan.participantId}`] = deleteField();
        if (Object.keys(out).length > 0)
            batch.update(ref(patch.collection, String(patch.id)), out);
    }
    try {
        await batch.commit();
        return ok(undefined);
    }
    catch (e) {
        return err(toDomainError(e));
    }
};
// The per-trip counterpart of a template delete's row cleanup (Doc 07 §5): "Remove"
// on an applied template detaches/deletes this trip's rows sourced from it and drops
// it from `importedTemplateIds`. Fire-and-forget like the import applier above, not
// awaited like sync/copy/participant-removal — the trip screen that triggered it is
// already streaming these same items/todos/trip docs, so a rule denial rolls back via
// the next snapshot rather than needing a reported failure.
export const makeTemplateRemovalApplier = (db, p) => (plan, tripId) => {
    const batch = writeBatch(db);
    const ref = (collection, id) => collection === "items" ? p.item(id) : p.todo(id);
    for (const a of plan.actions) {
        if (a.kind === "delete") {
            batch.delete(ref(a.collection, String(a.id)));
        }
        else if (a.kind === "makeIndependent") {
            batch.update(ref(a.collection, String(a.id)), { provenance: deleteField(), templateIds: deleteField() });
        }
        else {
            batch.update(ref(a.collection, String(a.id)), {
                provenance: provenanceToDto(a.provenance),
                templateIds: a.templateIds,
            });
        }
    }
    batch.update(p.trip(tripId), { importedTemplateIds: plan.tripPatch.importedTemplateIds });
    void batch.commit();
};
//# sourceMappingURL=importApplier.js.map